import { useState, useEffect } from "react";
import Header from "./Header/Header";
import Sidebar from "./Sidebar/Sidebar";
import { toast } from "react-toastify";
import { useWeb3ModalAccount } from "@web3modal/ethers/react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Oval } from "react-loader-spinner";
import apiService from "../core/Service/detail";
import { postMethod } from "../core/service/common.api";
import dsh_icon_one from "../assets/dsh_icon_one.webp";
import prt_two from "../assets/prt_two.webp";
import prt_three from "../assets/prt_three.webp";
import prt_four from "../assets/prt_four.webp";
import prt_asset from "../assets/prt_asset.webp";
// import portfolio_chart from "../assets/portfolio_chart.webp";
import tax_edit from "../assets/tax_edit.webp";
import PortfolioSkeleton from "./PortfolioSkeleton";

const Portfolio = () => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [taxReportLoading, setTaxReportLoading] = useState(false);

  // Get connected wallet address from Web3Modal
  const { address } = useWeb3ModalAccount();

  // Fetch REAL portfolio data from backend
  const fetchPortfolioData = async () => {
    try {
      setLoading(true);

      // Use connected wallet address or fallback
      const walletAddress = localStorage.getItem("userAddress");

      console.log("Fetching portfolio for wallet:", walletAddress);

      const data = {
        apiUrl: apiService.getUserPortfolio,
        payload: { walletAddress },
      };

      const resp = await postMethod(data);

      if (resp.status === true && resp.data) {
        console.log("Portfolio data received:", resp.data);
        setPortfolioData(resp.data);

        if (resp.data.assets.length === 0) {
          // toast.info(
          //   "No investments found. Start investing to build your portfolio!"
          // );
        }
      } else {
        toast.error(resp.message || "Failed to fetch portfolio data");
        setPortfolioData(getEmptyPortfolioData(walletAddress));
      }
    } catch (error) {
      console.error("Error fetching portfolio:", error);
      toast.error("Error loading portfolio data");
      setPortfolioData(getEmptyPortfolioData(address));
    } finally {
      setLoading(false);
    }
  };

  // Empty portfolio data structure
  const getEmptyPortfolioData = (walletAddress) => {
    return {
      totalPortfolioValue: 0,
      dailyChange: 0,
      totalReturns: 0,
      assetCount: 0,
      assets: [],
      walletAddress: walletAddress,
      message: "No investments found",
    };
  };

  // Generate tax report with REAL data
  const generateTaxReport = async () => {
    try {
      setTaxReportLoading(true);

      const walletAddress = address || localStorage.getItem("walletAddress");
      if (!walletAddress) {
        toast.error("Please connect your wallet first");
        return;
      }

      // Check if there are any assets to report
      if (!portfolioData || portfolioData.assets.length === 0) {
        toast.error("No investment data available for tax report");
        return;
      }

      const data = {
        apiUrl: apiService.generateTaxReport,
        payload: {
          walletAddress,
          year: new Date().getFullYear(),
        },
      };

      const resp = await postMethod(data);

      if (resp.status === true) {
        toast.success("Tax report generated successfully!");
        if (resp.data.reportUrl) {
          window.open(resp.data.reportUrl, "_blank");
        } else {
          toast.info("Tax report data processed successfully");
        }
      } else {
        toast.error(resp.message || "Failed to generate tax report");
      }
    } catch (error) {
      console.error("Error generating tax report:", error);
      toast.error("Error generating tax report");
    } finally {
      setTaxReportLoading(false);
    }
  };

  // Calculate portfolio metrics from REAL data
  const calculatePortfolioMetrics = (assets) => {
    if (!assets || !Array.isArray(assets) || assets.length === 0) {
      return { totalValue: 0, totalReturns: 0, dailyChange: 0 };
    }

    const totalValue = assets.reduce(
      (sum, asset) => sum + (asset.currentValue || 0),
      0
    );
    const totalReturns = assets.reduce(
      (sum, asset) => sum + (asset.totalReturns || 0),
      0
    );
    const dailyChange = assets.length > 0 ? portfolioData?.dailyChange || 0 : 0;

    return {
      totalValue,
      totalReturns,
      dailyChange: parseFloat(dailyChange.toFixed(2)),
    };
  };

  // Get safe assets array
  const getSafeAssets = () => {
    return portfolioData?.assets || [];
  };

  // Get active asset safely
  const getActiveAsset = () => {
    const assets = getSafeAssets();
    return assets[activeIndex] || assets[0] || null;
  };

  // Refresh portfolio data
  // const refreshPortfolio = () => {
  //   fetchPortfolioData();
  // };

  useEffect(() => {
    fetchPortfolioData();
  }, [address]); // Re-fetch when wallet address changes

  const assets = getSafeAssets();
  const activeAsset = getActiveAsset();
  const metrics = calculatePortfolioMetrics(assets);

  return (
    <>
      <Header />

      <main className="main_flx">
        <Sidebar />

        {loading ? (
          <>
            <PortfolioSkeleton />
          </>
        ) : (
          <div className="right_wrap pb-4">
            <div className="stake_top_box">
              <div className="stake_bx_grid">
                <div className="dashboard_box">
                  <img src={dsh_icon_one} alt="Total Portfolio Value" />
                  <span>Total Portfolio Value</span>
                  <h4>${metrics.totalValue.toLocaleString()}</h4>
                  <small className="text-white text-center d-block mt-2">
                    Based on current token prices
                  </small>
                </div>
                <div className="dashboard_box">
                  <img src={prt_two} alt="24h Change" />
                  <span>Overall Returns</span>
                  <h4
                    style={{
                      color: metrics.dailyChange >= 0 ? "#16A34A" : "#DC2626",
                    }}
                  >
                    {metrics.dailyChange >= 0 ? "+" : ""}
                    {metrics.dailyChange}%
                  </h4>
                  <small className="text-white text-center d-block mt-2">
                    Since investment
                  </small>
                </div>
                <div className="dashboard_box">
                  <img src={prt_three} alt="Total Returns" />
                  <span>Total Returns</span>
                  <h4>${metrics.totalReturns.toLocaleString()}</h4>
                  <small className="text-white text-center d-block mt-2">
                    Realized + Unrealized
                  </small>
                </div>
                <div className="dashboard_box">
                  <img src={prt_four} alt="Asset Count" />
                  <span>Asset Count</span>
                  <h4>{assets.length}</h4>
                  <small className="text-white text-center d-block mt-2">
                    Diversified investments
                  </small>
                </div>
              </div>
            </div>

            {/* Asset Allocation Section */}
            {assets.length > 0 ? (
              <div className="row g-4 prt_asset_row">
                <div className="col-xl-9">
                  <div className="prt_asset_lft">
                    <h4 className="prt_asset_title">
                      <span>
                        <img src={prt_asset} alt="Asset Allocation" />
                      </span>
                      Asset Allocation
                    </h4>

                    <div className="prt_asset_grid">
                      <div className="prt_grid_child prt_asset_tab">
                        {assets.map((asset, index) => (
                          <div
                            key={asset._id || index}
                            className={`prt_asset_flex ${
                              activeIndex === index ? "active" : ""
                            }`}
                            onClick={() => setActiveIndex(index)}
                          >
                            <span
                              style={{ backgroundColor: getAssetColor(index) }}
                            ></span>
                            {asset.fundName}
                            <small>
                              ${(asset.currentValue || 0).toLocaleString()}
                            </small>
                          </div>
                        ))}
                      </div>
                      <div className="prt_grid_child">
                        {/* <img src={portfolio_chart} alt="Portfolio Chart" /> */}

                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={assets}
                              dataKey="currentValue"
                              nameKey="fundName"
                              cx="50%"
                              cy="50%"
                              outerRadius={120}
                              label
                            >
                              {assets.map((asset, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={getAssetColor(index)}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) =>
                                `$${value.toLocaleString()}`
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {activeAsset && (
                        <div className="prt_grid_child prt_chart_box">
                          <h6>{activeAsset.fundName}</h6>
                          <div className="prt_chart_flx_wrap">
                            <div className="prt_chart_flx">
                              <span>Current Value</span>
                              <p>
                                $
                                {(
                                  activeAsset.currentValue || 0
                                ).toLocaleString()}
                              </p>
                            </div>
                            <div className="prt_chart_flx">
                              <span>Share of Portfolio</span>
                              <p>
                                {(activeAsset.shareOfPortfolio || 0).toFixed(1)}
                                %
                              </p>
                            </div>
                            <div className="prt_chart_flx">
                              <span>Total Returns</span>
                              <p
                                style={{
                                  color:
                                    activeAsset.totalReturns >= 0
                                      ? "#16A34A"
                                      : "#DC2626",
                                }}
                              >
                                $
                                {(
                                  activeAsset.totalReturns || 0
                                ).toLocaleString()}
                              </p>
                            </div>
                            <div className="prt_chart_flx">
                              <span>Tokens Held</span>
                              <p>
                                {(
                                  activeAsset.tokenAmount || 0
                                ).toLocaleString()}
                              </p>
                            </div>
                            <div className="prt_chart_flx">
                              <span>Current Token Price</span>
                              <p>${activeAsset.tokenPrice || "N/A"}</p>
                            </div>
                            <div className="prt_chart_flx">
                              <span>Recent Activity</span>
                              <h5>
                                {activeAsset.recentActivity ||
                                  "No recent activity"}
                              </h5>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tax Report Section */}
                <div className="col-xl-3 col-sm-7">
                  <div className="dsh_tax_box m-0">
                    <p>Generate tax-ready statements (1099 forms style)</p>
                    <div className="text-center">
                      <img src={tax_edit} alt="Tax Report" />
                    </div>
                    <div className="dsh_profile_btn_wrap mt-2">
                      <button
                        className="dsh_profile_btn"
                        onClick={generateTaxReport}
                        disabled={taxReportLoading}
                      >
                        {taxReportLoading
                          ? "Generating..."
                          : "Generate Tax Report"}
                      </button>
                    </div>
                    <p className="tax-note">
                      Includes all transactions for {new Date().getFullYear()}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-portfolio-section">
                {/* <div className="empty-portfolio">
                  <h4>Your Portfolio is Empty</h4>
                  <p>Start building your investment portfolio by purchasing your first asset.</p>
                  <button
                    className="dsh_profile_btn invest-now-btn"
                    onClick={() => window.location.href = '/market'}
                  >
                    Browse Available Assets
                  </button>
                </div> */}
                <div className="empty-investments mt-5">
                  <img
                    src={new URL("./../assets/no-invest.png", import.meta.url)}
                    alt="No investments"
                    className="no_inv_img"
                  />
                  <h5 className="no_iv_title">Your Portfolio is Empty</h5>
                  <p className="no_iv_cnt">
                    Start building your investment portfolio by purchasing your
                    first asset.
                  </p>
                  <button
                    className="invest-now-btn primary_btn"
                    // onClick={() => navigate("/market")}
                    onClick={() => (window.location.href = "/market")}
                  >
                    Browse Available Assets
                  </button>
                </div>
              </div>
            )}

            {/* Held Assets Grid */}
            {assets.length > 0 && (
              <>
                <h4 className="dsh_subtitle mt-4 mt-sm-5">Your Investments</h4>
                <div className="portfolio_held_grid">
                  {assets.map((asset) => {
                    const initial = asset.initialInvestment || 0;
                    const current = asset.currentValue || 0;
                    const profitPercentage =
                      initial > 0 ? ((current - initial) / initial) * 100 : 0;
                    const isProfit = profitPercentage >= 0;

                    return (
                      <div key={asset._id} className="portfolio_held_card">
                        <div className="dsh_inv_top">
                          <h4>{asset.fundName}</h4>
                        </div>
                        <img
                          src={asset.profileImage || dsh_icon_one}
                          alt={asset.fundName}
                          onError={(e) => {
                            e.target.src = dsh_icon_one;
                          }}
                        />
                        <h5>{asset.fundName}</h5>
                        <p>
                          {asset.description ||
                            `Investment in ${asset.fundName}`}
                        </p>

                        <div className="prt_hld_flx">
                          <span>Total Returns</span>
                          <h6
                            style={{ color: isProfit ? "#16A34A" : "#DC2626" }}
                          >
                            {isProfit ? "+" : ""}
                            {profitPercentage.toFixed(2)}%
                          </h6>
                        </div>

                        <div className="prt_hld_details">
                          <div className="prt_hld_detail_item">
                            <span>Invested:</span>
                            <strong>
                              ${(asset.initialInvestment || 0).toLocaleString()}
                            </strong>
                          </div>
                          <div className="prt_hld_detail_item">
                            <span>Current Value:</span>
                            <strong>
                              ${(asset.currentValue || 0).toLocaleString()}
                            </strong>
                          </div>
                          <div className="prt_hld_detail_item">
                            <span>Tokens:</span>
                            <strong>
                              {(asset.tokenAmount || 0).toLocaleString()}
                            </strong>
                          </div>
                        </div>

                        <div className="prt_hld_btn_wrap">
                          <button
                            className="dsh_inv_btn m-0"
                            onClick={() =>
                              (window.location.href = `/marketDetails/${asset._id}`)
                            }
                          >
                            <span>View Details</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </>
  );
};

// Helper function to generate asset colors
const getAssetColor = (index) => {
  const colors = [
    "#4F46E5",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#06B6D4",
    "#84CC16",
    "#F97316",
  ];
  return colors[index % colors.length];
};

export default Portfolio;
